import { useEffect, useState } from "react";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { clearAuthHeaders } from "api/instance";
import { loginPathFor } from "api/coachAuth";
import {
  COACH_AUTH_MODE,
  getCoachToken,
  refreshCoachToken,
} from "api/coachAuth";
import { userAtom } from "recoil/userAtom";

/**
 * 코치 JWT 세션 복원 게이트. 라우터보다 위에 두어, 화면이 그려지기 전에 메모리 토큰을 채운다.
 * - userAtom 이 "jwt" 표식을 갖고 있는데 메모리 토큰이 없으면(새로고침) 리프레시 쿠키로 재발급.
 *   실패(쿠키 만료·탈퇴)하면 프로필을 지우고 코치 로그인으로 보낸다.
 * - 레거시 토큰을 가진 코치 프로필(전환 이전 localStorage 잔존)은 토큰 필드를 떼고 재로그인시킨다.
 *   서버에서도 코치 레거시 토큰을 일괄 무효화하므로 어차피 쓸 수 없는 값이다.
 * 유저·앱 세션은 건드리지 않는다.
 */
export default function CoachSessionGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const user = useRecoilValue(userAtom);
  const resetUser = useResetRecoilState(userAtom);
  const needsRestore = user?.authMode === COACH_AUTH_MODE && !getCoachToken();
  const hasLegacyCoachToken = !!user?.accessToken && user?.role === "coach";
  const [ready, setReady] = useState(!needsRestore && !hasLegacyCoachToken);

  useEffect(() => {
    if (hasLegacyCoachToken) {
      clearAuthHeaders();
      resetUser(); // isReset → localStorage["user"] 삭제
      window.location.replace("/coach");
      return;
    }
    if (!needsRestore) {
      setReady(true);
      return;
    }
    let cancelled = false;
    refreshCoachToken().then((token) => {
      if (cancelled) return;
      if (!token) {
        const loginPath = loginPathFor(user); // 역할 우선: 코치 /coach, (내부자) 유저 역할이면 /login
        clearAuthHeaders();
        resetUser();
        window.location.replace(loginPath);
        return;
      }
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
    // 마운트 시 한 번만 판단한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) return null;
  return <>{children}</>;
}
